/**
 * Admin Conversation RPC definitions
 *
 * Admin endpoints for streaming chat - requires admin role.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, Unauthorized } from "../errors.ts";

// ============================================================================
// Request/Response Schemas
// ============================================================================

/**
 * Request schema for creating a conversation
 * @since 1.0.0
 */
const CreateConversationRequest = Schema.Struct({
  title: Schema.optional(Schema.String),
});

/**
 * Response schema for creating a conversation
 * @since 1.0.0
 */
const CreateConversationResponse = Schema.Struct({
  id: Schema.String,
});

/**
 * Request schema for streaming a message
 * @since 1.0.0
 */
const StreamMessageRequest = Schema.Struct({
  conversationId: Schema.String,
  content: Schema.String,
  systemPrompt: Schema.optional(Schema.String),
});

/**
 * Stream parts - text only (no tools)
 * @since 1.0.0
 */
export const ConversationPartSchema = Schema.Union(
  Schema.Struct({
    type: Schema.Literal("text-delta"),
    delta: Schema.String,
  }),
  Schema.Struct({
    type: Schema.Literal("finish"),
    reason: Schema.String,
  }),
  Schema.Struct({
    type: Schema.Literal("stream-done"),
    conversationId: Schema.String,
    messageId: Schema.String,
  }),
);

// ============================================================================
// Research Conversation Schemas
// ============================================================================

/**
 * Request schema for streaming a research conversation
 * @since 1.0.0
 */
const StreamResearchRequest = Schema.Struct({
  conversationId: Schema.String,
  content: Schema.String,
});

/**
 * Tool call schema for research conversation stream
 * @since 1.0.0
 */
const ToolCallPartSchema = Schema.Struct({
  type: Schema.Literal("tool-call"),
  id: Schema.String,
  name: Schema.String,
  params: Schema.Record({ key: Schema.String, value: Schema.Unknown }),
});

/**
 * Tool result schema for research conversation stream
 * @since 1.0.0
 */
const ToolResultPartSchema = Schema.Struct({
  type: Schema.Literal("tool-result"),
  id: Schema.String,
  name: Schema.String,
  result: Schema.Unknown,
});

/**
 * Stream parts for research conversation - includes tool-call and tool-result
 * @since 1.0.0
 */
export const ResearchConversationPartSchema = Schema.Union(
  Schema.Struct({
    type: Schema.Literal("text-delta"),
    delta: Schema.String,
  }),
  ToolCallPartSchema,
  ToolResultPartSchema,
  Schema.Struct({
    type: Schema.Literal("finish"),
    reason: Schema.String,
  }),
  Schema.Struct({
    type: Schema.Literal("stream-done"),
    conversationId: Schema.String,
    messageId: Schema.String,
  }),
);

/**
 * Type for research conversation stream parts
 * @since 1.0.0
 */
export type ResearchConversationPart = Schema.Schema.Type<typeof ResearchConversationPartSchema>;

/**
 * Type for conversation stream parts
 * @since 1.0.0
 */
export type ConversationPart = Schema.Schema.Type<typeof ConversationPartSchema>;

/**
 * Schema for conversation messages in history
 * @since 1.0.0
 */
const ConversationMessageSchema = Schema.Struct({
  id: Schema.String,
  role: Schema.Literal("user", "assistant", "system"),
  content: Schema.String,
});

/**
 * Request schema for getting conversation history
 * @since 1.0.0
 */
const GetHistoryRequest = Schema.Struct({
  conversationId: Schema.String,
});

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Conversation RPC group
 *
 * Provides generic conversation APIs for admin streaming chat:
 * - Create conversation
 * - Stream message (with LLM response)
 * - Stream research (with tool calls)
 * - Get history
 *
 * @since 1.0.0
 */
export class AdminConversationRpcs extends RpcGroup.make(
  Rpc.make("admin.conversation.create", {
    payload: CreateConversationRequest,
    success: CreateConversationResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.conversation.stream", {
    payload: StreamMessageRequest,
    success: ConversationPartSchema,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
    stream: true,
  }).middleware(AdminMiddleware),
  Rpc.make("admin.conversation.streamResearch", {
    payload: StreamResearchRequest,
    success: ResearchConversationPartSchema,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
    stream: true,
  }).middleware(AdminMiddleware),
  Rpc.make("admin.conversation.history", {
    payload: GetHistoryRequest,
    success: Schema.Array(ConversationMessageSchema),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
) {}
